import Chart from 'react-apexcharts';
import numeral from 'numeral';
import { Box, Card, CardContent, CardHeader, FormControlLabel, Grid, Skeleton, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useAuth from '../../../hooks/useAuth';
import { useCallback, useState, useEffect } from 'react';
import { dashboardApi } from '../../../api/dashboardApi';
import useMounted from '../../../hooks/useMounted';

const FinanceCostBreakdown = ({ selectedYear }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const mounted = useMounted();
  const colorsPie = ['#ff9800', '#ff4a5a', '#3f51b5', '#4db6ac', '#9c27b0', '#1a237e'];
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(1);
  const [professionsNumber, setProfessionsNumber] = useState([]);
  const [finished, setFinished] = useState(false);
useEffect(() => {
  const getCostBreakdown = async () => {
    setLoading(true);
    try {
      const data = await dashboardApi.getCostBreakdown(finished, selectedYear, localStorage.getItem('accessToken'));

      if (!data || data.length === 0 || !data.professionsNumber) {
        throw new Error('Impossible de récupérer les données ou données vides.');
      }
      console.log('FinanceCostBreakdown fetch (finished=' + finished + '):', data);

      if (mounted.current) {
        setProfessionsNumber(data.professionsNumber);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  }
  getCostBreakdown();
  }, [mounted, finished, selectedYear, reload]);

  useEffect(() => {

    const interval = setInterval(() => setReload(prevReload => prevReload + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  //  CHIFFRE D'AFFAIRE PAR PROFESSION POUR L'ADMIN
  const dataPro = {
    series: professionsNumber
      .filter((profession) => profession.somme !== null && Number(profession.somme) > 0)
      .map((profession, index) => ({
        color: colorsPie[index % colorsPie.length],
        dataPro: Number(profession.somme),
        label: `${profession.profession} (${profession.countCourses} thèmes, ${profession.countLearners} apprenants)`
      }))
  };
  //  NOMBRE DE VENTE PAR PROFESSION POUR LES COMMERCIAUX
  const data = {
    series: professionsNumber.map((profession, index) => ({ color: colorsPie[index % colorsPie.length], data: profession.countCourses, label: `${profession.profession} (${profession.countCourses} thèmes, ${profession.countLearners} apprenants)` }))
  };

  const chartOptions = {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    colors: colorsPie,
    dataLabels: {
      enabled: false
    },
    labels: data.series.map((item) => item.label),
    legend: {
      show: false
    },
    stroke: {
      colors: [theme.palette.background.paper],
      width: 1
    },
    theme: {
      mode: theme.palette.mode
    }
  };

  const chartOptionsPro = {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    colors: dataPro.series.map((item) => item.color),
    dataLabels: {
      enabled: true
    },
    labels: dataPro.series.map((item) => item.label),
    legend: {
      show: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65',
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              formatter(val) {
                return `${numeral(val).format('0,000,000.00').replaceAll(',', ' ')} €`;
              },
            },
            total: {
              show: true,
              showAlways: true,
              fontSize: '12px',
              formatter(w) {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return `${numeral(total).format('0,000,000.00').replaceAll(',', ' ')} €`;
              },
            }
          }
        }
      }
    },
    stroke: {
      colors: [theme.palette.background.paper],
      width: 1
    },
    theme: {
      mode: theme.palette.mode
    }
  };
  const chartSeries = data.series.map((item) => item.data);
  const chartSeriesPro = dataPro.series.map((item) => item.dataPro);
  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => theme.palette.mode === 'light' 
          ? '0 4px 18px 0 rgba(0,0,0,0.03)' 
          : '0 4px 18px 0 rgba(0,0,0,0.2)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {!loading ? (
        <Grid container alignItems="center" sx={{ px: 2, pt: 1 }}>
          <Grid item xs={7}>
            <CardHeader
              title={finished ? `Terminés par profession (${selectedYear})` : `CA par profession (${selectedYear})`}
              sx={{ px: 0, '& .MuiCardHeader-title': { fontSize: '1.05rem', fontWeight: 700 } }}
            />
          </Grid>
          <Grid item xs={5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={<Switch color="primary" />}
              checked={finished}
              onChange={(e) => setFinished(e.target.checked)}
              label="CA/Terminés"
              sx={{
                mr: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: 'text.secondary'
                }
              }}
            />
          </Grid>
        </Grid>
      ) : (
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ ml: 2, mt: 2 }}
          width={150}
          height={40}
        />
      )}
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
        {!loading ? (
          <Box sx={{ minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Chart
              height="250"
              width="100%"
              options={user.access_level === 0 ? chartOptionsPro : chartOptions}
              series={user.access_level === 0 ? chartSeriesPro : chartSeries}
              type="donut"
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Skeleton
              animation="wave"
              variant="circular"
              width={180}
              height={180}
            />
          </Box>
        )}

        {/* Legend container */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
          {/* L'AFFICHAGE DE NOMBRE DE VENTE PAR PROFESSION POUR LE COMMERCIAL */}
          {user.access_level !== 0 && data.series.map((item) => (
            <Box key={item.label}>
              {!loading ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    py: 1,
                    px: 1.5,
                    borderRadius: '10px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: item.color,
                      borderRadius: '50%',
                      height: 10,
                      width: 10,
                      boxShadow: `0 0 8px ${item.color}`,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    color="text.primary"
                    sx={{ ml: 2, fontWeight: 600, fontSize: '0.82rem' }}
                  >
                    {item.label}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography
                    color="text.secondary"
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: '0.82rem' }}
                  >
                    {numeral(item.data).format('0,000,000.00').replaceAll(',', ' ')}
                  </Typography>
                </Box>
              ) : (
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={32}
                />
              )}
            </Box>
          ))}

          {/* L'AFFICHAGE DE CHIFFRE D'AFFAIRE PAR PROFESSION POUR L'ADMIN */}
          {user.access_level === 0 && dataPro.series.map((item) => (
            <Box key={item.label}>
              {!loading ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    py: 1,
                    px: 1.5,
                    borderRadius: '10px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: item.color,
                      borderRadius: '50%',
                      height: 10,
                      width: 10,
                      boxShadow: `0 0 8px ${item.color}`,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    color="text.primary"
                    sx={{ ml: 2, fontWeight: 600, fontSize: '0.82rem' }}
                  >
                    {item.label}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography
                    color="text.secondary"
                    variant="subtitle2"
                    sx={{ fontWeight: 705, fontSize: '0.82rem' }}
                  >
                    {numeral(item.dataPro).format('0,000,000.00').replaceAll(',', ' ')} €
                  </Typography>
                </Box>
              ) : (
                <Skeleton
                  variant="text"
                  animation="wave"
                  height={32}
                />
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FinanceCostBreakdown;
